import React, { useContext, useEffect, useMemo } from "react";
import { NavLink } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { pathLeague } from "../../../config/routes";
import { AdPositionEnum, LanguageLocaleEnum } from "../../../client/api";
import { formatDate } from "../../../config/utils";
import EmptyState from "../EmptyState";
import { AdSlot } from "../AdSlot";
import { ClavaContext } from "../../../config/contexts";
import { RootState } from "../../../store";
import { fetchHomeMatches } from "../../../store/actions/matchActions";
import { CUP_ID, FAVORITE_ID, HomeSectionTitle } from "../../../config/matchSmallUtils";
import { showTranslated } from "../../../config/translator";
import CupSmall from "../Cup";
import MatchTile from "../Match/MatchTile";

type MatchesCardProps = {
  selectedDate: Date;
};

const MatchesCard: React.FC<MatchesCardProps> = ({ selectedDate }) => {
  const { l, aoi, user } = useContext(ClavaContext);
  const homeItems = useSelector<RootState, RootState["match"]["homeMatches"]>(s => s.match.homeMatches);

  const selectedDateStr = useMemo(() => formatDate(selectedDate, LanguageLocaleEnum.DE, true, false, true), [selectedDate]);

  const sections = useMemo(() => homeItems[`${aoi}-${user.sports}-${selectedDateStr}`] ?? [], [aoi, homeItems, selectedDateStr, user.sports]);
  const dispatch = useDispatch();
  useEffect(() => {
    if (!homeItems[`${aoi}-${user.sports}-${selectedDateStr}`]) {
      fetchHomeMatches(dispatch, aoi, user.sports, selectedDateStr);
    }
  }, [aoi, dispatch, homeItems, selectedDateStr, user.sports]);
  const renderSectionTitle = (title: HomeSectionTitle) => {
    const baseClasses =
      "text-font dark:text-font-dark font-medium text-sm text-left";

    if (typeof title.id === "number" && title.id !== FAVORITE_ID && title.id !== CUP_ID) {
      return (
        <NavLink
          to={pathLeague(title.id)}
          key={`home-title-${title.id}`}
          className={`${baseClasses} hover:text-primary dark:hover:text-primary transition-colors`}
        >
          {showTranslated(title.name, l)}
        </NavLink>
      );
    }

    return <div className={baseClasses}>{showTranslated(title.name, l)}</div>;
  };

  if (sections.length === 0) {
    const nothingOnHome = (sections.length ?? 0) === 0;

    if (nothingOnHome) {
      return (
        <EmptyState
          loading
          loadingMessageKey="loadingMatches"
          messageKey="noMatches"
          className="w-full"
        />
      );
    }
    return null;
  }
  return (
    <>
      {sections.map((section) => {
        if (typeof section === "string") {
          return <AdSlot key={`home-ad-${section}`} type={AdPositionEnum.HOME_MATCH_DOUBLE} />;
        }
        if ("name" in section && "type" in section) {
          return renderSectionTitle(section);
        }
        if ("dateFrom" in section) {
          return <CupSmall key={`home-tournament-${section.id}`} cup={section} />;
        }
        return <MatchTile key={`home-match-${section.id}`} match={section} />;
      })}
    </>
  );
};

export default MatchesCard;
